import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const empty = () => ({ name: '', location: '', assetId: '', notes: '' });

export function EquipmentModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const assets = useQuery({ queryKey: ['finance', 'fixed-assets'], queryFn: () => financeApi.listFixedAssets().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => financeApi.createEquipment(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'equipment'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) { setError(t('pm.error.name')); return; }
    save.mutate({ name: form.name.trim(), location: form.location || null, assetId: form.assetId || null, notes: form.notes || null });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('pm.eqModal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('pm.eq.name')}</span>
          <input className="input" value={form.name} onChange={set('name')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('pm.eq.location')}</span>
            <input className="input" value={form.location} onChange={set('location')} />
          </label>
          <label className="field">
            <span className="field-label">{t('pm.eq.asset')}</span>
            <select className="input" value={form.assetId} onChange={set('assetId')}>
              <option value="">{t('pm.eq.noAsset')}</option>
              {(assets.data || []).map((a) => <option key={a.id} value={a.id}>{a.assetNumber} · {a.name}</option>)}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('pm.eq.notes')}</span>
          <input className="input" value={form.notes} onChange={set('notes')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
